import Pusher, * as PusherTypes from 'pusher-js';
import React, { createContext, useContext, useRef, useState } from 'react';

import AuthenticationStore from '~/auth/authenticationStore';
import { getEnvVariable } from '~/dev/utils/getEnvVariable';
import { LOGIN_CONSTANTS } from '~/features/login/constants';
import Configuration from '~/services/configuration';
import { captureException } from '~/utils/sentry';

const authStore = AuthenticationStore();
type SubscribeCallbacks = {
  success: (data: PusherTypes.Members) => void;
  error?: (error: SubscriptionError) => void;
};
type ChannelsTable = {
  [index: string]: PusherTypes.Channel | PusherTypes.PresenceChannel;
};
interface Context {
  getPusher: () => PusherTypes.default;
  /** Function to subscribe to presence and private channels. callbacks
   * will be called after events of success or error are received after
   * trying to authenticate to pusher.
   */
  subscribe: (
    pusher: PusherTypes.default,
    channelName: string,
    callbacks: SubscribeCallbacks,
    joinType?: string,
  ) => PusherTypes.Channel;
  getChannels: (pusher: PusherTypes.default) => ChannelsTable;
  getPresenceChannelMembers: (
    pusher: PusherTypes.default,
    channelName: string,
    shouldUnsubscribeAfter: boolean,
    joinType?: string,
  ) => Promise<PusherTypes.Members>;
  /** Whether there is a connection error with Pusher */
  hasConnectionError: boolean;
}

interface SubscriptionError {
  type: string;
  error: string;
  status: number;
}

interface PusherData {
  code: number;
  message: string;
}

interface PusherError {
  error?: { type: string; data: PusherData };
  data?: PusherData;
  type: string;
}

/* https://pusher.com/docs/channels/using_channels/connection/#available-states */
export const PUSHER_STATE = {
  INITIALIZED: 'initialized',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  UNAVAILABLE: 'unavailable',
  FAILED: 'failed',
  DISCONNECTED: 'disconnected',
} as const;
export type PusherState = (typeof PUSHER_STATE)[keyof typeof PUSHER_STATE];

const createPusherInstance = (
  channelJoinTypesRef: React.RefObject<Map<string, string>>,
) => {
  const pusherAuthEndpoint = authStore.isRolodexAuthed()
    ? `${Configuration.rolodexEndpoint}/api/v1/pusher/auth`
    : `${Configuration.authEndpoint}/v1/auth/pusher-auth`;

  return new Pusher(getEnvVariable('PUSHER_APP_KEY'), {
    cluster: getEnvVariable('PUSHER_CLUSTER'),
    channelAuthorization: {
      endpoint: pusherAuthEndpoint,
      transport: 'ajax',
      customHandler: async (payload, callback) => {
        const { socketId, channelName } = payload;
        const joinType = channelJoinTypesRef.current?.get(channelName);
        const body = new URLSearchParams({
          socket_id: socketId,
          channel_name: channelName,
        });
        if (joinType) {
          body.append('join_type', joinType);
        }
        try {
          const response = await fetch(pusherAuthEndpoint, {
            method: 'post',
            body,
            credentials: 'include',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/x-www-form-urlencoded',
              ...(authStore.getCsrf()
                ? {
                    [LOGIN_CONSTANTS.HEADERS.CSRF_TOKEN]: authStore.getCsrf(),
                  }
                : {}),
              ...(authStore.getRefreshToken()
                ? {
                    Authorization: `Bearer ${authStore.getRefreshToken()}`,
                  }
                : {}),
            },
          });
          const token = await response.json();
          callback(null, token);
          channelJoinTypesRef.current?.delete(channelName);
        } catch (error: any) {
          callback(error, null);
          channelJoinTypesRef.current?.delete(channelName);
        }
      },
    },
  });
};

const PusherContext = createContext<Context>({
  getPusher: () => {
    throw new Error('Pusher not initialized');
  },
  subscribe: () => {
    throw new Error('Pusher not initialized');
  },
  getChannels: () => {
    throw new Error('Pusher not initialized');
  },
  getPresenceChannelMembers: () => {
    throw new Error('Pusher not initialized');
  },
  hasConnectionError: false,
});

export const PusherProvider = ({ children }: { children: React.ReactNode }) => {
  const pusherRef = useRef<PusherTypes.default | undefined>();
  // set joinType per channel in a ref so that the customHandler can access it and set it for the pusher-auth request at subscription time
  const channelJoinTypesRef = useRef<Map<string, string>>(new Map());
  const [hasConnectionError, setHasConnectionError] = useState(false);

  const getPusher = () => {
    if (!pusherRef.current) {
      pusherRef.current = createPusherInstance(channelJoinTypesRef);
      bindToPusherErrorEvent();
    }
    return pusherRef.current;
  };

  /**
   * Binds to pusher connection changes to detect if there is a connection error to Pusher.
   * Blocks the user from editing and reports the error to Sentry if the error is non-recoverable.
   *
   */
  function bindToPusherErrorEvent() {
    if (!pusherRef.current) return;

    pusherRef.current.connection.bind('error', (err: PusherError) => {
      // Detect connection limits, or a problem with the Pusher account connection that we should be notified about
      // Error Codes: https://pusher.com/docs/channels/library_auth_reference/pusher-websockets-protocol/#error-codes
      const errorCode = err?.error?.data?.code;
      const applicationDoesNotExist = errorCode === 4001;
      const applicationDisabled = errorCode === 4003;
      const applicationIsOverQuota = errorCode === 4004;
      if (
        applicationIsOverQuota ||
        applicationDoesNotExist ||
        applicationDisabled
      ) {
        setHasConnectionError(true);
        captureException(
          err?.error?.data?.message ??
            'Unexpected pusher connection error, check Pusher dashboard for possible application issues.',
          err,
          {
            section: 'pusher error',
          },
        );
      }
    });
  }

  function subscribe(
    pusher: PusherTypes.default,
    channelName: string,
    { success, error }: SubscribeCallbacks,
    joinType?: string,
  ) {
    if (joinType) {
      channelJoinTypesRef.current.set(channelName, joinType);
    }
    const channel = pusher.subscribe(channelName);
    channel.bind(
      'pusher:subscription_succeeded',
      (data: PusherTypes.Members) => {
        success?.(data);
      },
    );
    channel.bind(
      'pusher:subscription_error',
      (errorData: SubscriptionError) => {
        error?.(errorData);
      },
    );

    return channel;
  }

  function getPresenceChannelMembers(
    pusher: PusherTypes.default,
    channelName: string,
    shouldUnsubscribeAfter = false,
    joinType?: string,
  ): Promise<PusherTypes.Members> {
    return new Promise((resolve, reject) => {
      subscribe(
        pusher,
        channelName,
        {
          success: (data) => {
            shouldUnsubscribeAfter && pusher.unsubscribe(channelName);
            resolve(data);
          },
          error: (error) => reject(error),
        },
        joinType,
      );
    });
  }

  const context: Context = {
    getPusher,
    subscribe,
    getChannels: (pusher: PusherTypes.default) => pusher.channels.channels,
    getPresenceChannelMembers,
    hasConnectionError,
  };

  return (
    <PusherContext.Provider value={context}>{children}</PusherContext.Provider>
  );
};

export const usePusherContext = () => {
  const context = useContext(PusherContext);

  return context;
};
